import React, { useLayoutEffect, useState } from 'react';
import { Alert, Platform, Pressable, Text as RNText, ScrollView, StyleSheet } from 'react-native';

import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { EditLineIcon } from '@/assets/icons/EditLineIcon';
import { SettingsGearIcon } from '@/assets/icons/SettingsGearIcon';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { DevDemoStackParamList } from '@/feature/devsettings/DevDemoStack';
import { designSystem } from '@/styles/styles';

type Nav = NativeStackNavigationProp<DevDemoStackParamList>;

type BlurEffect =
    | 'systemUltraThinMaterial'
    | 'systemThinMaterial'
    | 'systemMaterial'
    | 'systemThickMaterial'
    | 'systemChromeMaterial'
    | 'dark'
    | 'light'
    | 'regular';

const BLUR_EFFECTS: BlurEffect[] = [
    'systemUltraThinMaterial',
    'systemThinMaterial',
    'systemMaterial',
    'systemThickMaterial',
    'systemChromeMaterial',
    'dark',
    'light',
    'regular',
];

const BLUR_PICKER_VARIANTS = new Set<string>();

type DemoMeta = {
    label: string;
    options: string;
    note: string;
    defaultBlur?: BlurEffect;
};

const DEMO_META: Record<string, DemoMeta> = {
    NavDemoHeaderDefault: {
        label: 'Default Header',
        options: 'headerShown: true\nheaderTintColor: white',
        note: 'Plain compact navigation bar with no large title or blur. This is the baseline React Navigation native-stack header.',
    },
    NavDemoHeaderDefaultTransparent: {
        label: 'Transparent Header',
        options: 'headerShown: true\nheaderTransparent: true\nheaderTintColor: white',
        note: 'The header background is fully transparent — scroll content passes beneath it with no blur or background tint. Works best against a solid screen background. Add headerBlurEffect to introduce a material blur.',
    },
    NavDemoHeaderLargeTitle: {
        label: 'Large Title',
        options:
            'headerShown: true\nheaderLargeTitle: true\nheaderTintColor: white\nheaderLargeTitleStyle: { color: white }',
        note: 'Large title displays below the navigation bar and collapses into the inline title as you scroll up. Standard iOS pattern seen in Settings, App Store, etc.',
    },
    NavDemoHeaderWithButtons: {
        label: 'Native Header Buttons',
        options:
            'headerShown: true\nheaderLargeTitle: true\nheaderLeft: () => <SettingsGearIcon>\nheaderRight: () => <EditLineIcon>',
        note: 'headerLeft replaces the default back chevron. headerRight appears on the trailing side. On iOS 26 the OS wraps these buttons in a liquid glass pill shape automatically.',
    },
};

const ITEMS = Array.from({ length: 22 }, (_, i) => `Item ${i + 1}`);

const HeaderLeftButton = () => (
    <Pressable onPress={() => Alert.alert('Left button', 'headerLeft replaces the back chevron')} hitSlop={12}>
        <SettingsGearIcon />
    </Pressable>
);

const HeaderRightButton = () => (
    <Pressable onPress={() => Alert.alert('Right button', 'headerRight lives on the trailing side')} hitSlop={12}>
        <EditLineIcon color={ds.colorValue.text.primary} />
    </Pressable>
);

export const NativeHeaderDemoScreen = () => {
    const navigation = useNavigation<Nav>();
    const route = useRoute();

    const meta = DEMO_META[route.name] ?? { label: route.name, options: '', note: '' };
    const showBlurPicker = BLUR_PICKER_VARIANTS.has(route.name);
    const [blurEffect, setBlurEffect] = useState<BlurEffect>(meta.defaultBlur ?? 'regular');

    useLayoutEffect(() => {
        if (route.name === 'NavDemoHeaderWithButtons') {
            navigation.setOptions({
                headerLeft: HeaderLeftButton,
                headerRight: HeaderRightButton,
            });
        }
    }, [navigation, route.name]);

    useLayoutEffect(() => {
        if (!showBlurPicker) return;
        navigation.setOptions({ headerBlurEffect: blurEffect });
    }, [navigation, blurEffect, showBlurPicker]);

    const activeOptions = showBlurPicker ? meta.options.replace('<picker>', blurEffect) : meta.options;

    return (
        <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scroll}
            contentInsetAdjustmentBehavior="automatic"
        >
            {showBlurPicker ? (
                <Box mb="s16">
                    <Text variant="labelSmall" color="neutral400" mb="s8">
                        Blur material
                    </Text>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                        <Box flexDirection="row" gap="s8">
                            {BLUR_EFFECTS.map(effect => (
                                <Pressable
                                    key={effect}
                                    onPress={() => setBlurEffect(effect)}
                                    style={[styles.chip, blurEffect === effect && styles.chipActive]}
                                >
                                    <RNText style={[styles.chipText, blurEffect === effect && styles.chipTextActive]}>
                                        {effect}
                                    </RNText>
                                </Pressable>
                            ))}
                        </Box>
                    </ScrollView>
                </Box>
            ) : null}

            <Box
                borderRadius="r8"
                padding="s16"
                gap="s8"
                backgroundColor={ds.colorToken.background.neutral.subtler}
                mb="s16"
            >
                <Text variant="labelSmall" color="neutral400" mb="s4">
                    Active options
                </Text>
                <RNText style={styles.code}>{activeOptions}</RNText>
            </Box>

            {meta.note ? (
                <Box mb="s24">
                    <Text variant="labelSmall" color="neutral400" mb="s4">
                        Note
                    </Text>
                    <Text variant="bodySmall" color={ds.colorToken.text.primary}>
                        {meta.note}
                    </Text>
                </Box>
            ) : null}

            <Box mb="s16">
                <Button
                    label="Push another level (see header animate)"
                    onPress={() => navigation.push(route.name as 'NavDemoHeaderDefault')}
                />
            </Box>

            <Box padding="s12" borderRadius="r8" backgroundColor={ds.colorToken.background.neutral.subtler} mb="s8">
                <Text variant="labelSmall" color="neutral500">
                    Scroll edge — at the top the header has no content behind it
                </Text>
            </Box>

            {ITEMS.map(item => (
                <Box
                    key={item}
                    padding="s16"
                    borderRadius="r8"
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    mb="s8"
                >
                    <Text variant="bodySmall" color="neutral300">
                        {item}
                    </Text>
                </Box>
            ))}
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    scrollView: {
        flex: 1,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
    scroll: {
        padding: 16,
        paddingBottom: 48,
    },
    code: {
        fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }),
        fontSize: 12,
        color: ds.colorValue.text.primary,
    },
    chip: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 16,
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
    chipActive: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
    },
    chipText: {
        fontSize: 11,
        color: designSystem.colors.neutral300,
        fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }),
    },
    chipTextActive: {
        color: ds.colorValue.text.primary,
    },
});
